import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Col, Button, Form } from "react-bootstrap";
import { get } from "lodash";

import { updateActuatorCommand } from "service/gatewayService";
import Pulse from "./Pulse";
import Level from "./Level";
import Loop from "./Loop";
import AnalogConfig from "./AnalogConfig";
import {
    ACTUATOR_CONFIG_SUCCESS_MESSAGE,
    ACTUATOR_INVALID_MESSAGE,
    MaxActuatorLimit,
    MAX_LOOP_LIMIT,
    NUM_OF_LOOPS_ERROR_MESSAGE,
    TRY_AGAIN_ERROR_MESSAGE,
} from "constant";
import HoverAuthorizeTooltip from "components/authorize/AuthorizeTooltip";
import { useActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import Pwm from "./PWM";
import ControlOptions from "./ControlOptions";
import ModeOptions from "./ModeOptions";
import GuardTimes from "./GuardTimes";
import { getActuatorConfigTemplate } from "utils/actuatorFunctions";
import {
    getAPIError,
    showErrorAlert,
    showInfoAlert,
    showSuccessAlert,
} from "utils/alert";
import { isHttpSuccess } from "utils/functions";
import { Actuator, ActuatorCommandMode } from "types/Actuator";
import { useModal } from "components/modals/ModalTemplate";

type IOControllerState = {
    isIOController: boolean;
    isAnalog: boolean;
    minmax: string[];
    unit: string;
    mode: string[];
};

const ActuatorConfiguration = () => {
    const {
        gatewayId,
        ldsuId: deviceId,
        said,
    }: { gatewayId: string; ldsuId: string; said: string } = useParams();

    const {
        setActuator,
        setEditedActuator,
        editedActuator,
        editedActuator: {
            auto,
            mode,
            cycle_count,
            timing,
            activate_value,
            deactivate_value,
        },
        isIOControllerState,
        editMode,
        isFourWired,
    }: {
        actuator: Actuator;
        setActuator: React.Dispatch<React.SetStateAction<Actuator>>;
        setEditedActuator: React.Dispatch<React.SetStateAction<Actuator>>;
        editedActuator: Actuator;
        isIOControllerState: IOControllerState;
        editMode: boolean;
        type: string;
        isFourWired: boolean;
    } = useActuatorConfigurationContext();

    // Modal State
    const { modal, openModal, setModalShow } = useModal();

    const { minmax, unit, isAnalog } = isIOControllerState || {};

    const [error, setError] = useState({
        hasError: false,
        message: "",
    });

    useEffect(() => {
        setError({ hasError: false, message: "" });
    }, [mode, cycle_count, timing, activate_value, deactivate_value]);

    useEffect(() => {
        if (editMode) {
            toast(
                <div>
                    <b className="title">Info</b>
                    <p className="description">
                        Configuration needs to be saved before it can be
                        {isFourWired ? " controlled" : " activated/deactivated"}
                        .
                    </p>
                </div>,
                {
                    type: "info",
                    theme: "light",
                    hideProgressBar: true,
                }
            );
        }
    }, [editMode, isFourWired]);

    const activationInfoReminder = () => {
        return showInfoAlert({
            message: (
                <>
                    To apply the new configuration, click {" "}
                    <span className="font-weight-bold text-primary-black-1">
                        ACTIVATE
                    </span>{" "}
                    button. The actuator will use the new configuration at the
                    next activation.
                </>
            ),
        });
    };

    const validatePulseMode = () => {
        const t1 = get(timing, "[0][0]");
        const t2 = get(timing, "[0][1]");

        if (
            !t1 ||
            !t2 ||
            t1 > MaxActuatorLimit ||
            t2 > MaxActuatorLimit ||
            Number(t1 + t2) > MaxActuatorLimit
        ) {
            return {
                hasError: true,
                message: ACTUATOR_INVALID_MESSAGE,
            };
        }
        return { hasError: false, message: "" };
    };

    const validateLoopMode = () => {
        const t1 = get(timing, "[0][0]");
        const t2 = get(timing, "[0][1]");

        if (!t1 || !t2 || Number(t1 + t2) > MaxActuatorLimit) {
            return {
                hasError: true,
                message: ACTUATOR_INVALID_MESSAGE,
            };
        }

        if (cycle_count && (cycle_count < 0 || cycle_count > MAX_LOOP_LIMIT)) {
            return {
                hasError: true,
                message: NUM_OF_LOOPS_ERROR_MESSAGE,
            };
        }

        return { hasError: false, message: "" };
    };

    const validateAnalogMode = () => {
        if (!isAnalog || !activate_value || !deactivate_value) {
            return { hasError: false, message: "" };
        }

        if (
            activate_value > Number(minmax[1]) ||
            activate_value < Number(minmax[0]) ||
            deactivate_value > Number(minmax[1]) ||
            deactivate_value < Number(minmax[0])
        ) {
            return {
                hasError: true,
                message: `Unable to save. Please ensure Activation/Deactivation Voltage is between ${minmax[0]}${unit} and ${minmax[1]}${unit}.`,
            };
        }

        return { hasError: false, message: "" };
    };

    const validatePWMMode = () => {
        // ensure timing pairs are valid first
        const timingError = validateTimingPairs(timing);
        if (timingError.hasError) {
            return timingError;
        }

        // validate cycle count if provided
        if (
            cycle_count != null &&
            (cycle_count < 0 || cycle_count > MAX_LOOP_LIMIT)
        ) {
            return {
                hasError: true,
                message: NUM_OF_LOOPS_ERROR_MESSAGE,
            };
        }

        return { hasError: false, message: "" };
    };

    const validateTimingPairs = (pairs: any) => {
        for (const pair of pairs || []) {
            const t1 = pair?.[0];
            const t2 = pair?.[1];

            if (!t1 || !t2 || Number(t1 + t2) > MaxActuatorLimit) {
                return {
                    hasError: true,
                    message: ACTUATOR_INVALID_MESSAGE,
                };
            }
        }
        return { hasError: false, message: "" };
    };

    const validateForm = () => {
        if (mode === ActuatorCommandMode.PULSE) {
            return validatePulseMode();
        }

        if (mode === ActuatorCommandMode.LOOP) {
            return validateLoopMode();
        }

        if (mode === ActuatorCommandMode.PWM) {
            return validatePWMMode();
        }

        return validateAnalogMode();
    };

    const showSaveConfirmationModal = () => {
        openModal({
            key: "save_actuator_configuration_modal",
            modalType: "custom",
            modalIcon: "help_outline",
            iconVariant: "primary",
            modalTitle: "Save Configuration?",
            modalContent: (
                <>
                    Saving a new configuration may deactivate any activation
                    that is currently in progress. The new settings will take
                    effect at the next activation.
                    <br />
                    Do you want to proceed?
                </>
            ),
            modalButton: (
                <>
                    <Button
                        variant="secondary"
                        style={{ color: "#ffffff" }}
                        onClick={() => setModalShow(false)}
                    >
                        CANCEL
                    </Button>
                    <Button onClick={saveConfig}>PROCEED</Button>
                </>
            ),
        });
    };

    const saveConfig = async () => {
        setModalShow(false);
        const validate = validateForm();

        if (validate.hasError) {
            setError(validate);
            return;
        }

        const body: any = getActuatorConfigTemplate({
            ...editedActuator,
            mode: isFourWired ? ActuatorCommandMode.MOTOR : mode, // if it's four wired, we will set mode to MOTOR_MODE as it is the only mode for four wired actuator, even if the UI will still show the original mode
        });

        const saveConfigRes: any = await updateActuatorCommand(
            gatewayId,
            deviceId,
            said,
            body
        );

        if (isHttpSuccess(saveConfigRes.status)) {
            const { data } = saveConfigRes;
            const edited = { ...editedActuator, ...data };
            setActuator(edited);
            setEditedActuator(edited);
            showSuccessAlert({
                message: ACTUATOR_CONFIG_SUCCESS_MESSAGE,
            });
            setError({ hasError: false, message: "" });

            if (!auto && !isFourWired) activationInfoReminder();
        } else {
            showErrorAlert(getAPIError(saveConfigRes, TRY_AGAIN_ERROR_MESSAGE));
        }
    };

    const renderMode = () => {
        // this function will only run if it's sensor actuator or digital output actuator for iocontroller
        switch (mode) {
            case ActuatorCommandMode.LEVEL:
                return <Level />;
            case ActuatorCommandMode.PULSE:
                return <Pulse />;
            case ActuatorCommandMode.LOOP:
                return <Loop />;
            case ActuatorCommandMode.PWM:
                return <Pwm />;

            // for IO controller
            default:
                // if digital output, return Level
                return <Level />;
        }
    };

    return (
        <Col lg={8} xs={12}>
            <Form>
                <fieldset>
                    <div className="form-box-head pb-4">
                        <h5 className="float-left">Configuration</h5>
                    </div>
                    <div className="form-box">
                        <ControlOptions />

                        {!isFourWired && <ModeOptions />}

                        {isIOControllerState.mode ||
                            !isIOControllerState.isIOController ||
                            (isIOControllerState.isIOController && !isAnalog) ? (
                            !isFourWired && renderMode()
                        ) : (
                            <AnalogConfig />
                        )}
                    </div>

                    <div className="form-box-footer">
                        {error.hasError ? (
                            <p className="text-danger mb-3" role="alert">
                                {error.message}
                            </p>
                        ) : (
                            ""
                        )}
                        <HoverAuthorizeTooltip permission="gateway:update">
                            <Button
                                variant="primary"
                                className="save-btn"
                                onClick={showSaveConfirmationModal}
                                disabled={!editMode}
                            >
                                SAVE
                            </Button>
                        </HoverAuthorizeTooltip>
                    </div>

                    {isFourWired && <GuardTimes />}
                </fieldset>
            </Form>
            {modal}
        </Col>
    );
};

export default ActuatorConfiguration;
